import { useState } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Select } from './Select';
import { FileDropzone } from './FileDropzone';
import { verifyHash } from '../services/hashService';
import { HashAlgorithm, EncodingFormat } from '../types';
import { translations } from '../i18n/hu';
import { CheckCircle, XCircle } from 'lucide-react';

const algorithms: { value: HashAlgorithm; label: string }[] = [
  { value: 'MD5', label: 'MD5' },
  { value: 'SHA-1', label: 'SHA-1' },
  { value: 'SHA-256', label: 'SHA-256' },
  { value: 'SHA-384', label: 'SHA-384' },
  { value: 'SHA-512', label: 'SHA-512' },
  { value: 'SHA3-256', label: 'SHA3-256' },
  { value: 'BLAKE2b', label: 'BLAKE2b' },
  { value: 'CRC32', label: 'CRC32' },
];

const encodings: { value: EncodingFormat; label: string }[] = [
  { value: 'hex', label: translations.encoding.hex },
  { value: 'hex-upper', label: translations.encoding.hexUpper },
  { value: 'base64', label: translations.encoding.base64 },
];

export function VerifyHash() {
  const [inputType, setInputType] = useState<'text' | 'file'>('text');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [expectedHash, setExpectedHash] = useState('');
  const [algorithm, setAlgorithm] = useState<HashAlgorithm>('SHA-256');
  const [encoding, setEncoding] = useState<EncodingFormat>('hex');
  const [verification, setVerification] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleFilesSelected = (selectedFiles: File[]) => {
    setFiles(selectedFiles);
  };

  const handleVerify = async () => {
    if (inputType === 'text' && !text.trim()) {
      setError('Kérjük, adjon meg szöveget');
      return;
    }
    if (inputType === 'file' && files.length === 0) {
      setError('Kérjük, válasszon ki egy fájlt');
      return;
    }
    if (!expectedHash.trim()) {
      setError('Kérjük, adja meg a várható hash-t');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const input = inputType === 'text' ? text : files[0];
      const result = await verifyHash(input, algorithm, expectedHash.trim(), encoding);
      setVerification(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba történt');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card title={translations.verification.title}>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant={inputType === 'text' ? 'primary' : 'outline'}
              onClick={() => {
                setInputType('text');
                setFiles([]);
                setVerification(null);
              }}
            >
              Szöveg
            </Button>
            <Button
              variant={inputType === 'file' ? 'primary' : 'outline'}
              onClick={() => {
                setInputType('file');
                setText('');
                setVerification(null);
              }}
            >
              Fájl
            </Button>
          </div>

          {inputType === 'text' ? (
            <Textarea
              label="Szöveg"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={translations.input.textPlaceholder}
              rows={6}
            />
          ) : (
            <FileDropzone
              onFilesSelected={handleFilesSelected}
              multiple={false}
            />
          )}

          <Textarea
            label={translations.verification.expectedHash}
            value={expectedHash}
            onChange={(e) => setExpectedHash(e.target.value)}
            placeholder={translations.verification.enterHash}
            rows={3}
            className="font-mono text-sm"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={translations.algorithm.label}
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value as HashAlgorithm)}
              options={algorithms}
            />
            <Select
              label={translations.encoding.label}
              value={encoding}
              onChange={(e) => setEncoding(e.target.value as EncodingFormat)}
              options={encodings}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button
            onClick={handleVerify}
            isLoading={isLoading}
            disabled={
              (inputType === 'text' && !text.trim()) ||
              (inputType === 'file' && files.length === 0) ||
              !expectedHash.trim()
            }
            className="w-full"
          >
            {translations.common.verify}
          </Button>
        </div>
      </Card>

      {verification && (
        <Card>
          <div className="space-y-4">
            <div
              className={`flex items-center gap-3 p-4 rounded-lg ${
                verification.match
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}
            >
              {verification.match ? (
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
              )}
              <div>
                <p
                  className={`font-semibold ${
                    verification.match
                      ? 'text-green-800 dark:text-green-300'
                      : 'text-red-800 dark:text-red-300'
                  }`}
                >
                  {verification.match
                    ? translations.verification.match
                    : translations.verification.noMatch}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {translations.verification.computedHash}
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="font-mono text-sm break-all text-gray-900 dark:text-white">
                  {verification.computedHash}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {translations.verification.expectedHash}
              </label>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="font-mono text-sm break-all text-gray-900 dark:text-white">
                  {verification.expectedHash}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

